import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ArrowLeftRight, ArrowUpRight, ChevronDown, ChevronRight, ClipboardList, DoorOpen, FileText, Package, TriangleAlert, UserRoundCheck, Wrench, X } from "lucide-react";
import { amount } from "./api";
import { protocolAttention, type ProtocolGroup, type ProtocolIssue } from "./protocolGrouping";
import { protocolPresentation } from "./protocolPresentation";
import { protocolQuestionType } from "./protocolQuestionType";
import "./protocol-grouped.css";

type Props = {
  groups: ProtocolGroup[];
  revisionId: string;
  multipleBuildings: boolean;
  renderIssue: (issue: ProtocolIssue) => ReactNode;
  openWork: (id: string) => void;
};

const states: Record<string, string> = {
  open: "Открыто",
  designer: "Проектировщику",
  confirmed: "Расхождение подтверждено",
  excluded: "Исключено специалистом",
  resolved: "Рассмотрено",
};

function questionCount(count: number): string {
  const last = count % 10;
  const tens = count % 100;
  return `${count} ${tens >= 11 && tens <= 14 ? "вопросов" : last === 1 ? "вопрос" : last >= 2 && last <= 4 ? "вопроса" : "вопросов"}`;
}

function valueText(side: NonNullable<ProtocolIssue["left"]>): string {
  return side.value == null ? "Не установлено" : `${amount(side.value)} ${side.unit}`.trim();
}

export function ProtocolGroupedView({ groups, revisionId, multipleBuildings, renderIssue, openWork }: Props) {
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [openIssues, setOpenIssues] = useState<Set<string>>(new Set());
  const [details, setDetails] = useState<ProtocolGroup | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const id = useId();

  useEffect(() => {
    setOpenGroups(new Set());
    setOpenIssues(new Set());
    setDetails(null);
  }, [revisionId]);

  useEffect(() => {
    if (details && !dialog.current?.open) dialog.current?.showModal();
    else if (!details && dialog.current?.open) dialog.current.close();
  }, [details]);

  const toggleGroup = (key: string) => setOpenGroups(previous => {
    const next = new Set(previous);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const toggleIssue = (key: string) => setOpenIssues(previous => {
    const next = new Set(previous);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const closeDetails = () => setDetails(null);

  const issueRow = (issue: ProtocolIssue) => {
    const expanded = openIssues.has(issue.id);
    const panelId = `${id}-issue-${encodeURIComponent(issue.id)}`;
    const presentation = protocolPresentation(issue);
    const question = protocolQuestionType(issue);
    const attention = protocolAttention(issue);
    const attentionLabels = attention?.split(" · ").filter(label => label !== question.label) || [];
    const needsVerification = Boolean(attention || presentation.limitation || question.kind === "missing");
    const limitations = [presentation.limitation, attention].filter(Boolean).join(" · ");
    const suggestedAction = issue.kind === "note" || question.kind === "missing"
      ? question.action || presentation.action : presentation.action || question.action;
    const check = presentation.limitation ? "Проверить полноту подсчёта" : suggestedAction || "Проверить замечание по источнику";
    const state = issue.decision_state || "open";
    const single = issue.left || issue.right;
    const TypeIcon = question.kind === "missing" ? TriangleAlert : question.kind === "comparison" ? ArrowLeftRight : ClipboardList;
    return <div key={issue.id} className={`pg-issue pg-issue--${question.kind}${expanded ? " is-expanded" : ""}${needsVerification ? " needs-verification" : ""}${single ? "" : " pg-issue--text"}`}>
      <button type="button" className="pg-issue-row" aria-expanded={expanded} aria-controls={expanded ? panelId : undefined} onClick={() => toggleIssue(issue.id)}>
        <ChevronRight size={17} className="pg-issue-chevron" aria-hidden="true" />
        <span className="pg-issue-name">
          <span className={`pg-question-type pg-question-type--${question.kind}`}><TypeIcon size={12} aria-hidden="true" />{question.label}</span>
          <strong>{presentation.title}</strong>
          {issue.scope && <span>{issue.scope}</span>}
          <span className={`pg-check${presentation.limitation ? " pg-check--attention" : ""}`}><UserRoundCheck size={13} aria-hidden="true" /><span><span className="pg-check-label">Что сделать: </span>{check}</span></span>
          {attentionLabels.length > 0 && <span className="pg-attention">{attentionLabels.join(" · ")}</span>}
        </span>
        {single && <span className="pg-values">
          {issue.left && issue.right ? <>
            <span className="pg-value-pair">
              <span className="pg-value-side"><small>{presentation.leftCaption}</small><strong>{valueText(issue.left)}</strong></span>
              <span className="pg-versus" aria-label="в сравнении с">↔</span>
              <span className="pg-value-side"><small>{presentation.rightCaption}</small><strong>{valueText(issue.right)}</strong></span>
            </span>
            <small>{issue.difference == null ? "Разница не определена" : `Разница ${amount(issue.difference)} ${issue.unit}`.trim()}</small>
          </> : <span className="pg-value-side"><small>{issue.left ? presentation.leftCaption : presentation.rightCaption}</small><strong>{valueText(single)}</strong></span>}
        </span>}
        <span className={`pg-state pg-state--${state in states ? state : "other"}`}>{states[state] || issue.status || state}</span>
      </button>
      {expanded && <div className="pg-issue-detail" id={panelId}>
        {limitations && <div className="pg-operator-notice"><UserRoundCheck size={18} aria-hidden="true" /><div><strong>Ограничения оснований</strong><p>{limitations}. Состояние решения не подтверждает полноту источников.</p></div></div>}
        <p className="pg-detail-action"><strong>Что сделать:</strong> {check}. Решение по замечанию и изменение объёма работы — отдельные действия.</p>
        {presentation.title !== issue.reason && <p className="pg-original-reason">В протоколе: {issue.reason}</p>}
        {renderIssue(issue)}
      </div>}
    </div>;
  };

  const questionSections = (issues: ProtocolIssue[], scopeKey: string) => {
    // Presentation-only partition: no merging, new issue IDs, or cross-work moves.
    const sections = [
      { key: "comparison", title: "Сравнение источников", Icon: ArrowLeftRight },
      { key: "clarification", title: "Что нужно уточнить", Icon: ClipboardList },
    ] as const;
    return sections.map(({ key, title, Icon }) => {
      const rows = issues.filter(issue => protocolQuestionType(issue).section === key);
      if (!rows.length) return null;
      const sectionId = `${id}-questions-${encodeURIComponent(scopeKey)}-${key}`;
      return <section className={`pg-question-section pg-question-section--${key}`} key={key} aria-labelledby={sectionId}>
        <h4 id={sectionId} className="pg-question-section-heading"><Icon size={15} aria-hidden="true" />{title}<span>{questionCount(rows.length)}</span></h4>
        <div className={`pg-columns${key === "clarification" ? " pg-columns--clarification" : ""}`} aria-hidden="true"><span>Вопрос и действие</span>{key === "comparison" && <span>Значения / данные</span>}<span>Состояние</span></div>
        {rows.map(issueRow)}
      </section>;
    });
  };

  return <>
    <div className="protocol-grouped">
      {groups.length > 0 && <div className="pg-toolbar">
        <span>Тип / марка → работа → вопросы</span>
        <button type="button" className="pg-text-button" onClick={() => { setOpenGroups(new Set()); setOpenIssues(new Set()); }} disabled={!openGroups.size && !openIssues.size}>Свернуть всё <ChevronDown size={14} aria-hidden="true" /></button>
      </div>}
      {groups.map(group => {
        const expanded = openGroups.has(group.key);
        const panelId = `${id}-group-${encodeURIComponent(group.key)}`;
        const attentionCount = group.issues.filter(issue => protocolAttention(issue) != null || protocolPresentation(issue).limitation != null || protocolQuestionType(issue).kind === "missing").length;
        return <section className={`pg-entity${expanded ? " is-open" : " is-collapsed"}`} key={group.key}>
          <div className="pg-entity-heading">
            <button type="button" className="pg-entity-toggle" aria-expanded={expanded} aria-controls={expanded ? panelId : undefined} onClick={() => toggleGroup(group.key)}>
              <span className="pg-entity-symbol">{group.general ? <FileText size={24} aria-hidden="true" /> : group.category === "Двери" ? <DoorOpen size={25} aria-hidden="true" /> : <Package size={24} aria-hidden="true" />}</span>
              <span className="pg-entity-title"><strong>{group.product}</strong>{group.mark && <span>{group.mark}</span>}{multipleBuildings && <small>{group.building || "Корпус не указан"}</small>}</span>
              <span className="pg-entity-counts"><span>{questionCount(group.issues.length)}</span>{attentionCount > 0 && <small><UserRoundCheck size={13} aria-hidden="true" />Проверить основания: {attentionCount}</small>}</span>
              <ChevronDown size={19} className="pg-entity-chevron" aria-hidden="true" />
            </button>
            <button type="button" className="pg-entity-more" onClick={() => setDetails(group)} aria-label={`Подробнее: ${group.product} ${group.mark}`.trim()}>Подробнее</button>
          </div>
          {expanded && <div id={panelId} className="pg-entity-content">
            {group.general ? questionSections(group.issues, group.key) : group.works.map(work => <div className="pg-work" key={work.id || work.issues[0]?.id}>
              <div className="pg-work-heading">
                <div><span className="pg-work-label"><Wrench size={13} aria-hidden="true" />Связанная работа</span><h3 title={work.name}>{work.name || "Наименование работы не указано"}</h3></div>
                {work.id && <button type="button" className="pg-text-button" onClick={() => openWork(work.id!)}>Открыть работу <ArrowUpRight size={14} aria-hidden="true" /></button>}
              </div>
              {questionSections(work.issues, `${group.key}-${work.id || work.issues[0]?.id}`)}
            </div>)}
          </div>}
        </section>;
      })}
    </div>
    <dialog ref={dialog} className="protocol-grouped-dialog" aria-labelledby={`${id}-details-title`} onClose={closeDetails} onClick={event => { if (event.target === event.currentTarget) closeDetails(); }}>
      {details && <div className="pg-dialog-content">
        <div className="pg-dialog-heading"><div><span className="pg-dialog-label">Сведения о группе</span><h2 id={`${id}-details-title`}>{details.product}{details.mark && <span> · {details.mark}</span>}</h2></div><button type="button" className="pg-dialog-close" onClick={closeDetails} aria-label="Закрыть сведения"><X size={20} aria-hidden="true" /></button></div>
        <dl className="pg-facts"><div><dt>Раздел / подраздел</dt><dd>{[details.discipline, details.category].filter(Boolean).join(" / ") || "Не указано"}</dd></div>{multipleBuildings && <div><dt>Корпус / объект</dt><dd>{details.building || 'Корпус не указан'}</dd></div>}<div><dt>Вопросы в текущей выборке</dt><dd>{questionCount(details.issues.length)}</dd></div></dl>
        <h3>{details.general ? "Контекст замечаний" : "Связанные работы"}</h3>
        <ul className="pg-details-works">{details.works.map((work, index) => <li key={work.id || `context-${index}`}><p>{work.name || "Работа не указана"}</p>{work.id && <button type="button" className="pg-text-button" onClick={() => { closeDetails(); openWork(work.id!); }}>Открыть работу <ArrowUpRight size={14} aria-hidden="true" /></button>}</li>)}</ul>
        <p className="pg-dialog-note">Группа объединяет замечания для просмотра. Каждое решение сохраняется отдельно; объём работы не изменяется.</p>
      </div>}
    </dialog>
  </>;
}
